export const RING_COUNT = 6;
const RING_SPAN_DEG = 360;
const sin = (d: number) => Math.sin((d * Math.PI) / 180);

export type ThinkingIconParams = {
    // Min/max tilt angle (degrees) for the breathe animation's scaleX range.
    minPhi?: number;
    maxPhi?: number;
    // Additive offset (degrees) toward flat. 0 = unchanged, 90 = fully flat circles.
    tilt?: number;
    // Inner and outer arm radius in SVG units.
    rMin?: number;
    rMax?: number;
    // Breathe phase offset (0-100). Higher values spread arm timing further apart.
    phase?: number;
    // Breathe cycle multiplier - how many full breathe cycles fit in one phase period.
    phaseN?: number;
    // Precess intensity (0-100). Controls the wobble amplitude of each arm.
    precess?: number;
    // Precess cycle multiplier - how many precess cycles fit in one duration period.
    precessN?: number;
    // Base animation duration in seconds.
    dur?: number;
    // Whether the back half-arc uses a dashed stroke.
    dashed?: boolean;
};

export const DEFAULTS: Required<ThinkingIconParams> = {
    minPhi: 8,
    maxPhi: 52,
    tilt: 0,
    rMin: 34,
    rMax: 112,
    phase: 0,
    phaseN: 1,
    precess: 0,
    precessN: 1,
    dur: 6,
    dashed: false,
};

export const PRESETS = {
    'idle-wandering': {
        minPhi: 8,
        maxPhi: 52,
        rMin: 34,
        rMax: 112,
        phase: 100,
        phaseN: 2,
        precess: 0,
        precessN: 1,
        dur: 10,
    },
    'idle-attentive': {
        minPhi: 9,
        maxPhi: 20,
        rMin: 34,
        rMax: 112,
        phase: 14,
        phaseN: 1,
        precess: 5,
        precessN: 3,
        dur: 10,
    },
} as const satisfies Record<string, ThinkingIconParams>;

export type PresetName = keyof typeof PRESETS;

const MAX_PRECESS_ANGLE = 22;

export type RingStyle = {
    transform: string;
    breatheVars: Record<string, string>;
    precessVars: Record<string, string>;
};

export function computeStyles(params?: ThinkingIconParams): {
    svgVars: Record<string, string>;
    rings: RingStyle[];
} {
    const p = { ...DEFAULTS, ...params };

    const minPhi = Math.min(p.minPhi + p.tilt, 90);
    const maxPhi = Math.min(p.maxPhi + p.tilt, 90);
    const breatheDur = p.dur;
    const precessDur = p.dur / p.precessN;
    const phaseNorm = p.phase / 100;
    const precessNorm = p.precess / 100;
    const precessAmplitude = precessNorm * MAX_PRECESS_ANGLE;

    const svgVars: Record<string, string> = {
        '--minS': sin(minPhi).toFixed(4),
        '--maxS': sin(maxPhi).toFixed(4),
        '--dur': breatheDur + 's',
        '--pdur': precessDur.toFixed(4) + 's',
    };

    const rings: RingStyle[] = [];
    for (let i = 0; i < RING_COUNT; i++) {
        const t = i / RING_COUNT;
        const radius = p.rMin + (p.rMax - p.rMin) * (i / (RING_COUNT - 1));
        const angle = i * (RING_SPAN_DEG / RING_COUNT);
        const breatheDelay = -phaseNorm * breatheDur * p.phaseN * t;
        const precessDelay = -precessNorm * precessDur * p.precessN * t;

        rings.push({
            transform: `rotate(${angle.toFixed(3)}) scale(${radius.toFixed(2)})`,
            breatheVars: { '--bd': breatheDelay.toFixed(4) + 's' },
            precessVars: {
                '--pa': precessAmplitude.toFixed(2) + 'deg',
                '--pd': precessDelay.toFixed(4) + 's',
            },
        });
    }

    return { svgVars, rings };
}
